// The entrypoints register responsePolicy.ts so this never imports the router. The
// fetch is raced against its abort signal, which CapacitorHttp ignores on native except for GET.

import { ApiError, type ApiErrorBody } from "./apiError";
import { requestContext } from "./requestContext";

// Above the server's 30s route timeout, so a route that spends its budget still answers.
export const DEFAULT_REQUEST_TIMEOUT_MS = 35_000;
// Device waits (validation gives the agent 60s, the server 75s), SAML refresh, audit export.
export const LONG_REQUEST_TIMEOUT_MS = 90_000;
// A licence update: three licensing calls of up to 30s each, then a settings reload.
export const LICENSING_REQUEST_TIMEOUT_MS = 120_000;

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type ParamValue = string | number | boolean | Date;
export type Params = Record<string, ParamValue | readonly ParamValue[] | null | undefined>;

export interface RequestOptions {
  method?: HttpMethod;
  // Null and undefined are skipped. An array repeats the key.
  params?: Params;
  // A string is sent as is, labelled JSON unless `headers` says otherwise.
  body?: unknown;
  headers?: Record<string, string>;
  responseType?: "json" | "text" | "blob";
  signal?: AbortSignal;
  // Milliseconds; null for none.
  timeout?: number | null;
  // false leaves the response policy out (a probe that expects 401s).
  policy?: boolean;
}

export interface HttpResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

// Rejects marked handled when it acted, never settles when navigating away, and
// resolves when the caller should see the error.
export type ResponsePolicy = (error: ApiError) => Promise<void> | void;

let responsePolicy: ResponsePolicy | undefined;

export function setResponsePolicy(policy: ResponsePolicy | undefined): void {
  responsePolicy = policy;
}

function buildQuery(params: Params | undefined): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    const values: readonly ParamValue[] = Array.isArray(value) ? value : [value as ParamValue];
    for (const v of values) search.append(key, v instanceof Date ? v.toISOString() : String(v));
  }
  return search.toString();
}

function withQuery(url: string, params: Params | undefined): string {
  const query = buildQuery(params);
  if (!query) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${query}`;
}

function encodeBody(body: unknown, headers: Record<string, string>): BodyInit | undefined {
  if (body === undefined) return undefined;
  const hasType = Object.keys(headers).some((k) => k.toLowerCase() === "content-type");
  if (
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer
  ) {
    return body;
  }
  if (!hasType) headers["Content-Type"] = "application/json";
  return typeof body === "string" ? body : JSON.stringify(body);
}

function parseText(text: string): unknown {
  if (text === "") return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// AbortSignal.any needs Safari 17.4 and the app supports iOS 16.4.
function linkSignals(signals: AbortSignal[]): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const listeners: Array<() => void> = [];
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      break;
    }
    const onAbort = () => controller.abort(s.reason);
    s.addEventListener("abort", onAbort, { once: true });
    listeners.push(() => s.removeEventListener("abort", onAbort));
  }
  return { signal: controller.signal, dispose: () => listeners.forEach((off) => off()) };
}

interface Attempt {
  url: string;
  method: HttpMethod;
  init: RequestInit;
  responseType: NonNullable<RequestOptions["responseType"]>;
  signal?: AbortSignal;
  timeout: number | null;
  policy: boolean;
}

async function send<T>(attempt: Attempt): Promise<HttpResponse<T>> {
  const { url, method } = attempt;
  const timeoutController = new AbortController();
  let timedOut = false;
  const timer =
    attempt.timeout != null && attempt.timeout > 0
      ? setTimeout(() => {
          timedOut = true;
          timeoutController.abort(new DOMException(`${method} ${url} timed out`, "TimeoutError"));
        }, attempt.timeout)
      : undefined;
  const linked = linkSignals(attempt.signal ? [attempt.signal, timeoutController.signal] : [timeoutController.signal]);
  const signal = linked.signal;

  const stopped = new Promise<never>((_, reject) => {
    if (signal.aborted) reject(signal.reason);
    else signal.addEventListener("abort", () => reject(signal.reason), { once: true });
  });
  // Swallow the rejection when nothing is racing it any more.
  stopped.catch(() => {});
  const raced = <R>(p: Promise<R>) => Promise.race([p, stopped]);

  const failure = (cause: unknown): ApiError => {
    if (timedOut) return new ApiError({ kind: "timeout", method, url, cause });
    if (signal.aborted) return new ApiError({ kind: "aborted", method, url, cause });
    return new ApiError({ kind: "network", method, url, cause });
  };

  try {
    let response: Response;
    try {
      response = await raced(fetch(url, { ...attempt.init, signal }));
    } catch (cause) {
      throw failure(cause);
    }

    if (!response.ok) {
      let body: ApiErrorBody | string | null = null;
      try {
        body = parseText(await raced(response.text())) as ApiErrorBody | string | null;
      } catch {
        // The status is the answer; a body that could not be read adds nothing.
      }
      const error = new ApiError({
        kind: "http",
        status: response.status,
        body,
        headers: response.headers,
        method,
        url,
      });
      if (attempt.policy && responsePolicy) await responsePolicy(error);
      throw error;
    }

    let data: unknown;
    try {
      if (attempt.responseType === "blob") {
        data = await raced(response.blob());
      } else {
        const text = await raced(response.text());
        data = attempt.responseType === "text" ? (text === "" ? null : text) : parseText(text);
      }
    } catch (cause) {
      throw failure(cause);
    }
    return { data: data as T, status: response.status, headers: response.headers };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    linked.dispose();
  }
}

async function prepare(path: string, options: RequestOptions, external: boolean): Promise<Attempt> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};
  let url = path;
  let credentials: RequestCredentials = "omit";
  if (!external) {
    const context = await requestContext();
    Object.assign(headers, context.headers);
    url = `${context.baseUrl}${path}`;
    credentials = context.credentials;
  }
  Object.assign(headers, options.headers);
  const body = encodeBody(options.body, headers);
  return {
    url: withQuery(url, options.params),
    method,
    init: { method, headers, body, credentials },
    responseType: options.responseType ?? (external ? "text" : "json"),
    signal: options.signal,
    timeout: options.timeout === undefined ? DEFAULT_REQUEST_TIMEOUT_MS : options.timeout,
    policy: !external && options.policy !== false,
  };
}

export async function requestRaw<T>(path: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
  return send<T>(await prepare(path, options, false));
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await requestRaw<T>(path, options)).data;
}

type BodylessOptions = Omit<RequestOptions, "method" | "body">;
type BodyOptions = Omit<RequestOptions, "method">;

export const http = {
  get: <T>(path: string, options?: BodylessOptions) => request<T>(path, { ...options, method: "GET" }),
  delete: <T = void>(path: string, options?: BodyOptions) => request<T>(path, { ...options, method: "DELETE" }),
  post: <T = void>(path: string, body?: unknown, options?: BodylessOptions) =>
    request<T>(path, { ...options, body, method: "POST" }),
  put: <T = void>(path: string, body?: unknown, options?: BodylessOptions) =>
    request<T>(path, { ...options, body, method: "PUT" }),
  patch: <T = void>(path: string, body?: unknown, options?: BodylessOptions) =>
    request<T>(path, { ...options, body, method: "PATCH" }),
};

export async function download(
  path: string,
  options: BodylessOptions = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await requestRaw<Blob>(path, { ...options, responseType: "blob" });
  return { blob: response.data, filename: filenameFrom(response.headers.get("content-disposition")) };
}

function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // Fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(disposition);
  return plain ? (plain[2] ?? plain[1]).trim() : null;
}

// For hosts other than ours (IP discovery): no base URL, headers, credentials or response policy.
export async function external<T = string>(url: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
  return send<T>(await prepare(url, options, true));
}
